import { Popover } from '@mantine/core';
import { useMantineColorScheme } from '@mantine/core';
import { cloneElement, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { Avatar } from '@/components/ri/Avatar';
import { I, type IconProps } from '@/components/ri/icons';
import { useAuth } from '@/hooks/useAuth';

type SchemeChoice = 'light' | 'dark' | 'auto';

const SCHEME_LABELS: Record<SchemeChoice, string> = {
  light: 'Light',
  dark: 'Dark',
  auto: 'System',
};

export function UserMenuPopover() {
  const { user, logout } = useAuth();
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);

  const name = user?.displayName ?? 'User';
  const email = user?.email ?? '';
  const role = user?.role ?? 'Rep';
  const photoUrl = user?.photoUrl ?? null;

  const go = (to: string) => {
    setOpened(false);
    navigate(to);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={272}
      withinPortal
      shadow="lg"
      radius={12}
      offset={6}
    >
      <Popover.Target>
        <button
          onClick={() => setOpened((o) => !o)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            background: 'transparent',
            border: 0,
            borderRadius: 8,
            cursor: 'pointer',
            padding: '4px 6px',
            color: 'var(--text-primary)',
          }}
        >
          <Avatar name={name} src={photoUrl} size={28} />
          <span className="ri-mono" style={{ color: 'var(--text-tertiary)' }}>
            {cloneElement(I.chevD as ReactElement<IconProps>, { size: 14 })}
          </span>
        </button>
      </Popover.Target>

      <Popover.Dropdown
        p={0}
        style={{ background: 'var(--surface-overlay)', border: '1px solid var(--border)', overflow: 'hidden' }}
      >
        <div
          style={{
            padding: '14px 14px 12px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <Avatar name={name} src={photoUrl} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{name}</div>
            <div className="ri-mono" style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
              {email}
            </div>
            <div style={{ marginTop: 6 }}>
              <span
                style={{
                  fontSize: 10.5,
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontWeight: 600,
                  background: 'var(--accent-soft)',
                  color: 'var(--accent-text)',
                }}
              >
                {role}
              </span>
            </div>
          </div>
        </div>

        <div style={{ padding: 4 }}>
          <MenuRow icon={I.cog as ReactElement<IconProps>} label="Settings" onClick={() => go('/settings')} />
        </div>

        <div style={{ padding: '8px 10px', borderTop: '1px solid var(--border-subtle)' }}>
          <div className="ri-eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>
            Appearance
          </div>
          <div
            style={{
              display: 'flex',
              background: 'var(--surface-sunken)',
              borderRadius: 8,
              padding: 2,
              gap: 2,
              border: '1px solid var(--border)',
            }}
          >
            {(['light', 'dark', 'auto'] as SchemeChoice[]).map((s) => {
              const sel = s === colorScheme;
              return (
                <button
                  key={s}
                  onClick={() => setColorScheme(s)}
                  style={{
                    flex: 1,
                    height: 28,
                    padding: '0 8px',
                    fontSize: 12,
                    background: sel ? 'var(--surface-raised)' : 'transparent',
                    color: sel ? 'var(--text-primary)' : 'var(--text-secondary)',
                    border: 0,
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: sel ? 600 : 500,
                    boxShadow: sel ? 'var(--shadow-xs)' : 'none',
                  }}
                >
                  {SCHEME_LABELS[s]}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ padding: 4, borderTop: '1px solid var(--border-subtle)' }}>
          <button
            onClick={() => {
              setOpened(false);
              logout();
            }}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              border: 0,
              borderRadius: 7,
              cursor: 'pointer',
              background: 'transparent',
              color: 'var(--danger)',
              fontSize: 13,
              textAlign: 'left',
              minHeight: 36,
              fontWeight: 500,
            }}
          >
            {cloneElement(I.arrow as ReactElement<IconProps>, { size: 16 })}
            Sign out
          </button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}

function MenuRow({
  icon,
  label,
  onClick,
}: {
  icon: ReactElement<IconProps>;
  label: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        border: 0,
        borderRadius: 7,
        cursor: 'pointer',
        background: 'transparent',
        color: 'var(--text-primary)',
        fontSize: 13,
        textAlign: 'left',
        minHeight: 36,
      }}
    >
      {cloneElement(icon, { size: 16 })}
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  );
}
